import { DateTime } from "luxon";
import { BaseFirestoreData, PathParam } from "../util/base-firestore";
import { cloneDeep, isEqual, omit, pick } from "lodash";

// ユーザー作成時
// Auth作成、メールでパスワード送る

// ユーザー削除時
// Authから削除、ただし自分は消せない

// ユーザー更新時
// Authのカスタムクレームを更新

export class User extends BaseFirestoreData {
  // 変更不可
  organizationId: string;
  userId: string;
  email: string;

  // 変更可
  name: string;

  // 権限、カスタムクレームと連動する
  manage_organization: boolean;
  edit_client: boolean;

  quotation: {
    read: "all" | "assigned";
    write: "all" | "assigned" | "none";
    submit: "all" | "assigned" | "none";
  };
  invoice: {
    read: "all" | "assigned";
    write: "all" | "assigned" | "none";
    submit: "all" | "assigned" | "none";
  };
  deliveryNote: {
    read: "all" | "assigned";
    write: "all" | "assigned" | "none";
    submit: "all" | "assigned" | "none";
  };
}

export const PermissionKeys = ["manage_organization", "edit_client", "quotation", "invoice", "deliveryNote"] as const;
export type Priset = {
  name: string;
  description: string;
} & Pick<User, (typeof PermissionKeys)[number]>;

export const prisets: Priset[] = [
  {
    name: "業務担当者",
    description: "資料の作成や担当に割り振られている資料の更新ができます。",
    manage_organization: false,
    edit_client: false,
    quotation: {
      read: "assigned",
      write: "assigned",
      submit: "none",
    },
    invoice: {
      read: "assigned",
      write: "assigned",
      submit: "none",
    },
    deliveryNote: {
      read: "assigned",
      write: "assigned",
      submit: "none",
    },
  },
  {
    name: "部門管理者",
    description:
      "資料の作成や担当に割り振られている資料の更新に加え、取引先管理や取引先管理に資料を送信することができます。",
    manage_organization: false,
    edit_client: true,
    quotation: {
      read: "assigned",
      write: "assigned",
      submit: "assigned",
    },
    invoice: {
      read: "assigned",
      write: "assigned",
      submit: "assigned",
    },
    deliveryNote: {
      read: "assigned",
      write: "assigned",
      submit: "assigned",
    },
  },
  {
    name: "閲覧者",
    description: "すべてのデータを閲覧できます。税理士等におすすめの権限です。",
    manage_organization: false,
    edit_client: false,
    quotation: {
      read: "all",
      write: "none",
      submit: "none",
    },
    invoice: {
      read: "all",
      write: "none",
      submit: "none",
    },
    deliveryNote: {
      read: "all",
      write: "none",
      submit: "none",
    },
  },
  {
    name: "全権管理者",
    description: "すべての権限を持ちます。管理者や経営者におすすめの権限です。",
    manage_organization: true,
    edit_client: true,
    quotation: {
      read: "all",
      write: "all",
      submit: "all",
    },
    invoice: {
      read: "all",
      write: "all",
      submit: "all",
    },
    deliveryNote: {
      read: "all",
      write: "all",
      submit: "all",
    },
  },
];

export const userDefault = {
  email: "",
  name: "",
  ...pick(prisets[0], PermissionKeys),
} as const;

export const getEmptyUser = (): User => {
  return {
    organizationId: "",
    userId: "",
    email: "",
    name: "",
    ...pick(prisets[0], PermissionKeys),
    createdAt: DateTime.now(),
    updatedAt: DateTime.now(),
    deletedAt: null,
  };
};

export function getPresetName(user: User): string {
  for (const priset of prisets) {
    if (isEqualPermission(user, priset)) {
      return priset.name;
    }
  }
  return "";
}

export function isEqualPermission(user: User, priset: Priset): boolean {
  return isEqual(pick(user, PermissionKeys), pick(priset, PermissionKeys));
}

export function assignPermission(user: User, priset: Priset) {
  for (const key of PermissionKeys) {
    user[key] = cloneDeep(priset[key]) as any;
  }
}

export class UserCollectionPathParam extends PathParam {
  organizationId: string;
}
export class UserDocumentPathParam extends PathParam {
  organizationId: string;
  userId: string;
}

export const UserPath = [
  {
    path: "organizations/{organizationId}/users",
    primaryKey: "userId",
    param: UserCollectionPathParam,
    class: User,
    isCollection: true,
  },
  {
    path: "organizations/{organizationId}/users/{userId}",
    primaryKey: "userId",
    param: UserDocumentPathParam,
    class: User,
  },
] as const;
